import React, { useCallback, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { apiService } from '../services/apiService';
import AtsVacancySelect from '../relevanter/components/AtsVacancySelect';
import { getAtsConnectionStatus } from '../relevanter/services/api';
import toast from 'react-hot-toast';

export type EstaffLinkGuardResult = {
  estaffVacancyId?: string;
  estaffEnabled: boolean;
  skippedReason?: 'missing_position' | 'integration_disabled' | 'company_unavailable' | 'missing_link' | 'user_skipped';
};

/**
 * TASKNEIROKLYUCH-435 FR-1: guard-хук для проверки привязки позиции к вакансии E-Staff
 * перед созданием рассылки.
 *
 * Поведение по ТЗ:
 *   «При создании рассылки проверяется есть ли в E-Staff вакансия указанная в метке.
 *    Если нет или метка пустая, то выдается информационное сообщение и поле,
 *    чтобы ввести название вакансии.»
 *
 * Алгоритм:
 *   1. Если у компании E-Staff выключен — возвращает undefined без модалки.
 *   2. Если у позиции уже есть source-link на ESTAFF — возвращает externalVacancyId сразу.
 *   3. Иначе открывает модалку с сообщением + полем поиска E-Staff вакансии (AtsVacancySelect).
 *   4. После выбора сохраняет source-link в позиции через apiService.addSourceLink
 *      и возвращает externalVacancyId.
 *   5. Если пользователь закрыл модалку без выбора — возвращает undefined
 *      (рассылка создаётся без привязки к E-Staff, синхронизация не сработает).
 *
 * Использование в родительском компоненте:
 *
 *   const { ensureEstaffLink, guardModal } = useEstaffLinkGuard();
 *   // ...
 *   const handleCreate = async () => {
 *     const estaffVacancyId = await ensureEstaffLink(positionId);
 *     await createCampaign({ ...payload, estaffVacancyId });
 *   };
 *   return (<>...<button onClick={handleCreate}>Создать</button>{guardModal}</>);
 */
export function useEstaffLinkGuard() {
  const [modalState, setModalState] = useState<{
    open: boolean;
    positionId: number | null;
  }>({ open: false, positionId: null });

  // Ref для хранения Promise-resolver во время показа модалки
  const resolverRef = useRef<((value: string | undefined) => void) | null>(null);

  /** Проверяет подключение E-Staff и source-link без открытия модалки. */
  const checkEstaffLinkStatus = useCallback(async (
    positionId: number | null | undefined,
  ): Promise<EstaffLinkGuardResult> => {
    if (!positionId) {
      return { estaffEnabled: false, skippedReason: 'missing_position' };
    }

    // Источник истины для включённой ATS-интеграции — HR-Proxy connector.
    // companies.estaffEnabled в business-back исторический флаг и на dev/prod
    // может быть false при реально подключённом E-Staff.
    try {
      const estaffConnection = await getAtsConnectionStatus('estaff');
      if (!estaffConnection?.configured) {
        return { estaffEnabled: false, skippedReason: 'integration_disabled' };
      }
    } catch (error) {
      console.warn('useEstaffLinkGuard: getAtsConnectionStatus failed, skipping E-Staff guard', error);
      return { estaffEnabled: false, skippedReason: 'company_unavailable' };
    }

    // Шаг 1: интеграция включена, проверяем существующую привязку к E-Staff вакансии.
    try {
      const existing = await apiService.resolveSourceVacancyId(positionId, 'ESTAFF');
      if (existing) {
        return { estaffVacancyId: existing, estaffEnabled: true };
      }
    } catch (error) {
      console.warn('useEstaffLinkGuard: getSourceLinks failed', error);
    }

    return { estaffEnabled: true, skippedReason: 'missing_link' };
  }, []);

  /** Вызывается перед createCampaign. Возвращает ID привязки и признак включённой интеграции. */
  const ensureEstaffLinkWithStatus = useCallback(async (
    positionId: number | null | undefined,
  ): Promise<EstaffLinkGuardResult> => {
    const status = await checkEstaffLinkStatus(positionId);
    if (!status.estaffEnabled || status.estaffVacancyId || status.skippedReason !== 'missing_link') {
      return status;
    }

    // Шаг 2: интеграция включена, привязки нет → модалка с выбором вакансии
    return new Promise<EstaffLinkGuardResult>((resolve) => {
      resolverRef.current = (estaffVacancyId) => {
        resolve(estaffVacancyId
          ? { estaffVacancyId, estaffEnabled: true }
          : { estaffEnabled: true, skippedReason: 'user_skipped' });
      };
      setModalState({ open: true, positionId: positionId ?? null });
    });
  }, [checkEstaffLinkStatus]);

  /** Backward-compatible API для старых call sites. */
  const ensureEstaffLink = useCallback(async (positionId: number | null | undefined): Promise<string | undefined> => {
    const result = await ensureEstaffLinkWithStatus(positionId);
    return result.estaffVacancyId;
  }, [ensureEstaffLinkWithStatus]);

  const handleSelect = useCallback(async (atsVacancyId: string, _atsVacancyTitle: string) => {
    const positionId = modalState.positionId;
    if (!positionId || !atsVacancyId) return;

    try {
      // Сохраняем source-link чтобы в следующий раз модалка не показывалась
      await apiService.addSourceLink(positionId, 'ESTAFF', atsVacancyId);
    } catch (error: any) {
      console.error('useEstaffLinkGuard: addSourceLink failed', error);
      toast.error('Не удалось сохранить привязку к E-Staff, рассылка будет создана без неё');
    }

    // Резолвим Promise с выбранным ID и закрываем модалку
    const resolver = resolverRef.current;
    resolverRef.current = null;
    setModalState({ open: false, positionId: null });
    if (resolver) resolver(atsVacancyId);
  }, [modalState.positionId]);

  const handleSkip = useCallback(() => {
    // Пользователь закрыл модалку без выбора — продолжаем без sync
    const resolver = resolverRef.current;
    resolverRef.current = null;
    setModalState({ open: false, positionId: null });
    if (resolver) resolver(undefined);
  }, []);

  const guardModal = (
    <Dialog.Root
      open={modalState.open}
      onOpenChange={(open) => {
        if (!open) handleSkip();
      }}
    >
      {modalState.open && (
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50 z-[60]" />
          <Dialog.Content className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-xl p-6 w-full max-w-lg z-[60]">
            <Dialog.Title className="text-lg font-semibold text-gray-900 mb-2">
              Вакансия в E-Staff не указана
            </Dialog.Title>
            <Dialog.Description className="text-gray-600 mb-4">
              Чтобы статусы кандидатов синхронизировались в E-Staff, укажите соответствующую
              вакансию E-Staff для этой позиции. Настройка сохранится и будет использоваться
              для всех последующих рассылок.
            </Dialog.Description>
            <div className="mb-6">
              <AtsVacancySelect
                source="estaff"
                value=""
                selectedTitle=""
                onChange={({ atsVacancyId, atsVacancyTitle }) => {
                  handleSelect(atsVacancyId, atsVacancyTitle);
                }}
                onClear={() => { /* no-op — clearing не применимо в guard flow */ }}
              />
            </div>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={handleSkip}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Пропустить
              </button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      )}
    </Dialog.Root>
  );

  return { ensureEstaffLink, ensureEstaffLinkWithStatus, checkEstaffLinkStatus, guardModal };
}
